import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, CircularProgress, Chip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Stack, Card, CardContent, Autocomplete, TextField, Collapse, useTheme
} from '@mui/material';
import {
  Refresh as RefreshIcon, ExpandMore as ExpandIcon, ExpandLess as CollapseIcon,
  Inventory as ItemIcon, Storefront as BranchIcon, Warehouse as WarehouseIcon,
  LocalShipping as SupplierIcon, BadgeOutlined as CustodyIcon,
  Cake as BakeryIcon, Inbox as OpeningIcon,
  FileDownload as ExcelIcon, PictureAsPdf as PdfIcon, Print as PrintIcon
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import axiosInstance from '../../services/axiosInstance';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface BranchOpt { branchId: number; branchName: string; branchNameAr?: string; branchType?: string; }
interface SourceDto { sourceType: string; sourceTypeAr: string; sourceId?: number; sourceName: string; sourceNameAr: string; quantity: number; totalValue: number; avgUnitCost: number; documentCount: number; }
interface ItemDto { itemId: number; itemCode: string; itemName: string; itemNameAr: string; unitId: number; unitName: string; unitNameAr: string; totalQuantity: number; totalValue: number; sources: SourceDto[]; }
interface ReportDto { branchId?: number; branchName?: string; branchNameAr?: string; fromDate?: string; toDate?: string; items: ItemDto[]; grandTotalValue: number; totalUniqueItems: number; totalSourceLines: number; }

const BranchReceiptsReport: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { minDate, maxDate } = useFiscalDateRange();

  const T = (en: string, ar: string) => isRTL ? ar : en;
  const reportTitle = T('Branch Receipts Report', 'تقرير وارد الفرع');

  const [branches, setBranches] = useState<BranchOpt[]>([]);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [report, setReport] = useState<ReportDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [search, setSearch] = useState('');
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => {
    companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {});
    axiosInstance.get('/Branches').then(r => {
      const arr = Array.isArray(r.data) ? r.data : (r.data?.data || []);
      setBranches(arr.map((b: any) => ({
        branchId: b.branchId ?? b.BranchId,
        branchName: b.branchName ?? b.BranchName,
        branchNameAr: b.branchNameAr ?? b.BranchNameAr,
        branchType: b.branchType ?? b.BranchType
      })));
    }).catch(() => {});
  }, []);

  const loadReport = async () => {
    if (!branchId) { toast.error(T('Select a branch first', 'اختر الفرع أولاً')); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('branchId', String(branchId));
      if (fromDate) params.set('fromDate', fromDate.toISOString().split('T')[0]);
      if (toDate) params.set('toDate', toDate.toISOString().split('T')[0]);
      const { data } = await axiosInstance.get(`/BranchReceiptsReport?${params}`);
      setReport(data);
      setExpanded({});
    } catch (err: any) {
      toast.error(err.response?.data?.message || T('Failed to load report', 'فشل تحميل التقرير'));
    } finally { setLoading(false); }
  };

  const filteredItems = useMemo(() => {
    if (!report) return [];
    const q = search.trim().toLowerCase();
    if (!q) return report.items;
    return report.items.filter(i =>
      i.itemCode.toLowerCase().includes(q) ||
      i.itemName.toLowerCase().includes(q) ||
      (i.itemNameAr || '').includes(q) ||
      i.sources.some(s => s.sourceName.toLowerCase().includes(q) || s.sourceNameAr.includes(q))
    );
  }, [report, search]);

  const fmt = (n: number, d = 3) => Number(n).toFixed(d);
  const fmtKWD = (n: number) => n.toFixed(3) + ' KWD';
  const compName = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
  const selectedBranch = branches.find(b => b.branchId === branchId);
  const selectedBranchName = selectedBranch ? (isRTL ? (selectedBranch.branchNameAr || selectedBranch.branchName) : selectedBranch.branchName) : '';

  const sourceColor = (st: string) => {
    switch (st) {
      case 'Warehouse': return '#3b82f6';
      case 'Supplier': return '#10b981';
      case 'Custody': return '#f59e0b';
      case 'Bakery': return '#ec4899';
      case 'OpeningBalance': return '#7c3aed';
      case 'BranchTransfer': return '#06b6d4';
      default: return '#6b7280';
    }
  };
  const sourceIcon = (st: string) => {
    switch (st) {
      case 'Warehouse': return <WarehouseIcon fontSize="small" />;
      case 'Supplier': return <SupplierIcon fontSize="small" />;
      case 'Custody': return <CustodyIcon fontSize="small" />;
      case 'Bakery': return <BakeryIcon fontSize="small" />;
      case 'OpeningBalance': return <OpeningIcon fontSize="small" />;
      case 'BranchTransfer': return <BranchIcon fontSize="small" />;
      default: return <ItemIcon fontSize="small" />;
    }
  };

  // ============= Export Excel =============
  const exportExcel = async () => {
    if (!report) return;
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet(reportTitle);
      if (companyInfo?.logoBase64) {
        try {
          const img = wb.addImage({ base64: companyInfo.logoBase64.split(',').pop() || companyInfo.logoBase64, extension: 'png' });
          ws.addImage(img, { tl: { col: 0, row: 0 }, ext: { width: 100, height: 80 } });
        } catch {}
      }
      ws.mergeCells('C1:H1'); const cc = ws.getCell('C1'); cc.value = compName || ''; cc.font = { size: 18, bold: true, color: { argb: 'FFf97316' } }; cc.alignment = { horizontal: 'center' };
      ws.mergeCells('A3:H3'); const tc = ws.getCell('A3'); tc.value = `${reportTitle} — ${selectedBranchName}`; tc.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } }; tc.alignment = { horizontal: 'center' }; tc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFf97316' } };

      const headers = [T('Item Code', 'كود الصنف'), T('Item Name', 'اسم الصنف'), T('Unit', 'الوحدة'), T('Source Type', 'نوع المصدر'), T('Source', 'المصدر'), T('Quantity', 'الكمية'), T('Avg Cost', 'متوسط التكلفة'), T('Total Value', 'القيمة الإجمالية')];
      const hr = ws.getRow(5); headers.forEach((h, i) => { const c = hr.getCell(i + 1); c.value = h; c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFf97316' } }; c.alignment = { horizontal: 'center' }; });

      let row = 6;
      filteredItems.forEach(it => {
        it.sources.forEach((src, idx) => {
          const r = ws.getRow(row++);
          r.getCell(1).value = idx === 0 ? it.itemCode : '';
          r.getCell(2).value = idx === 0 ? (isRTL ? it.itemNameAr : it.itemName) : '';
          r.getCell(3).value = idx === 0 ? (isRTL ? it.unitNameAr : it.unitName) : '';
          r.getCell(4).value = isRTL ? src.sourceTypeAr : src.sourceType;
          r.getCell(5).value = isRTL ? src.sourceNameAr : src.sourceName;
          r.getCell(6).value = src.quantity;
          r.getCell(7).value = src.avgUnitCost;
          r.getCell(8).value = src.totalValue;
        });
        // Item subtotal row
        const rt = ws.getRow(row++);
        rt.getCell(5).value = T('Item Total', 'إجمالي الصنف');
        rt.getCell(6).value = it.totalQuantity;
        rt.getCell(8).value = it.totalValue;
        rt.eachCell(c => { c.font = { bold: true }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } }; });
      });
      ws.columns = [{ width: 14 }, { width: 28 }, { width: 10 }, { width: 14 }, { width: 22 }, { width: 14 }, { width: 14 }, { width: 16 }];
      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `BranchReceipts_${selectedBranchName}_${Date.now()}.xlsx`; a.click(); URL.revokeObjectURL(a.href);
      toast.success(T('Exported', 'تم التصدير'));
    } catch { toast.error(T('Export failed', 'فشل التصدير')); }
  };

  const exportPdf = () => {
    if (!report) return;
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pw = doc.internal.pageSize.width;
      doc.setFillColor(255, 247, 237); doc.rect(0, 0, pw, 45, 'F');
      if (companyInfo?.logoBase64) { try { doc.addImage(companyInfo.logoBase64, 'PNG', 12, 8, 30, 25); } catch {} }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(249, 115, 22);
      doc.text(compName || '', pw / 2, 15, { align: 'center' });
      doc.setFillColor(249, 115, 22); doc.roundedRect(pw / 2 - 60, 32, 120, 10, 3, 3, 'F');
      doc.setFontSize(11); doc.setTextColor(255, 255, 255); doc.text(`${reportTitle} - ${selectedBranchName}`, pw / 2, 38.5, { align: 'center' });

      const rows: any[][] = [];
      filteredItems.forEach(it => {
        it.sources.forEach((src, idx) => {
          rows.push([
            idx === 0 ? it.itemCode : '', idx === 0 ? it.itemName : '',
            idx === 0 ? it.unitName : '', src.sourceType, src.sourceName,
            fmt(src.quantity), fmt(src.avgUnitCost, 4), fmt(src.totalValue)
          ]);
        });
        rows.push(['', '', '', '', 'TOTAL', fmt(it.totalQuantity), '', fmt(it.totalValue)]);
      });
      autoTable(doc, {
        head: [['Code', 'Name', 'Unit', 'Source Type', 'Source', 'Qty', 'Avg Cost', 'Value']],
        body: rows, startY: 50, theme: 'grid',
        headStyles: { fillColor: [249, 115, 22], textColor: [255, 255, 255], fontSize: 9, halign: 'center' },
        bodyStyles: { fontSize: 8 }
      });
      doc.save(`BranchReceipts_${selectedBranchName}_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(T('Exported', 'تم التصدير'));
    } catch { toast.error(T('Export failed', 'فشل التصدير')); }
  };

  const handlePrint = () => {
    if (!report) return;
    const w = window.open('', '_blank', 'width=1100,height=800'); if (!w) return;
    const rows = filteredItems.map(it => {
      const sourceRows = it.sources.map(s => `<tr><td></td><td></td><td></td><td>${isRTL ? s.sourceTypeAr : s.sourceType}</td><td>${isRTL ? s.sourceNameAr : s.sourceName}</td><td style="text-align:right">${fmt(s.quantity)}</td><td style="text-align:right">${fmt(s.avgUnitCost, 4)}</td><td style="text-align:right">${fmt(s.totalValue)}</td></tr>`).join('');
      const head = `<tr class="item-head"><td><b>${it.itemCode}</b></td><td>${isRTL ? it.itemNameAr : it.itemName}</td><td>${isRTL ? it.unitNameAr : it.unitName}</td><td colspan="5"></td></tr>`;
      const tot = `<tr class="item-total"><td colspan="5" style="text-align:${isRTL ? 'left' : 'right'}"><b>${T('Item Total', 'إجمالي الصنف')}</b></td><td style="text-align:right"><b>${fmt(it.totalQuantity)}</b></td><td></td><td style="text-align:right"><b>${fmt(it.totalValue)}</b></td></tr>`;
      return head + sourceRows + tot;
    }).join('');
    w.document.write(`<html dir="${isRTL ? 'rtl' : 'ltr'}"><head><title>${reportTitle}</title><style>
      body{font-family:'Segoe UI','Tajawal',sans-serif;margin:18px;color:#333}
      .header{display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #f97316;padding-bottom:10px;margin-bottom:15px}
      .header img{max-height:60px} .header h1{color:#f97316;margin:0;font-size:22px}
      table{width:100%;border-collapse:collapse;font-size:11px}
      th{background:#f97316;color:white;padding:8px;text-align:${isRTL ? 'right' : 'left'}}
      td{padding:6px 8px;border:1px solid #ddd}
      .item-head td{background:#fff7ed;font-weight:bold} .item-total td{background:#fef3c7;font-weight:bold}
    </style></head><body>
    <div class="header"><div>${companyInfo?.logoBase64 ? `<img src="${companyInfo.logoBase64}"/>` : ''}</div>
    <div style="text-align:center;flex:1"><h1>${reportTitle}</h1><div style="color:#666;font-size:12px">${compName || ''} - ${selectedBranchName}</div></div><div style="width:80px"></div></div>
    <table><thead><tr><th>${T('Code', 'كود')}</th><th>${T('Name', 'اسم الصنف')}</th><th>${T('Unit', 'الوحدة')}</th><th>${T('Source Type', 'نوع المصدر')}</th><th>${T('Source', 'المصدر')}</th><th>${T('Qty', 'كمية')}</th><th>${T('Avg Cost', 'متوسط تكلفة')}</th><th>${T('Value', 'القيمة')}</th></tr></thead><tbody>${rows}</tbody></table>
    <div style="text-align:center;margin-top:15px;color:#999;font-size:10px">${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</div>
    </body></html>`);
    w.document.close(); setTimeout(() => w.print(), 500);
  };

  return (
    <Box sx={{ p: 3, direction: isRTL ? 'rtl' : 'ltr' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          {reportTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {T('Per-branch goods received — totals per item with source breakdown (warehouses, suppliers, custodies, bakery, opening, transfers). Includes all statuses.',
             'وارد الفرع — إجماليات لكل صنف مع تفصيل المصادر (مخازن، موردين، عُهد، المخبز، افتتاحي، تحويلات). يشمل كل الحالات.')}
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <Autocomplete size="small" sx={{ minWidth: 240 }}
            options={branches}
            getOptionLabel={(o) => isRTL ? (o.branchNameAr || o.branchName) : o.branchName}
            value={branches.find(b => b.branchId === branchId) || null}
            onChange={(_, v) => setBranchId(v?.branchId || '')}
            renderInput={(params) => <TextField {...params} label={T('Branch', 'الفرع')} />}
          />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker label={T('From', 'من تاريخ')} value={fromDate} onChange={(d) => setFromDate(d)}
              format="dd/MM/yyyy" minDate={minDate} maxDate={toDate || maxDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }} />
            <DatePicker label={T('To', 'إلى تاريخ')} value={toDate} onChange={(d) => setToDate(d)}
              format="dd/MM/yyyy" minDate={fromDate || minDate} maxDate={maxDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }} />
          </LocalizationProvider>
          <Button variant="contained" size="large" startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            onClick={loadReport} disabled={loading || !branchId}
            sx={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', borderRadius: 3, px: 3 }}>
            {loading ? T('Loading...', 'جاري التحميل...') : T('Load', 'تحميل')}
          </Button>
          {report && (
            <>
              <Button startIcon={<ExcelIcon />} onClick={exportExcel} variant="contained" sx={{ background: 'linear-gradient(45deg,#4CAF50 30%,#66BB6A 90%)', borderRadius: 3 }}>Excel</Button>
              <Button startIcon={<PdfIcon />} onClick={exportPdf} variant="contained" sx={{ background: 'linear-gradient(45deg,#f44336 30%,#e57373 90%)', borderRadius: 3 }}>PDF</Button>
              <Button startIcon={<PrintIcon />} onClick={handlePrint} variant="outlined" sx={{ borderRadius: 3 }}>{T('Print', 'طباعة')}</Button>
            </>
          )}
        </Stack>
      </Paper>

      {report && (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Card sx={{ flex: 1, minWidth: 200, background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', color: 'white', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>{T('Branch', 'الفرع')}</Typography>
                <Typography variant="h5" fontWeight="bold">{selectedBranchName}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1, minWidth: 200, background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: 'white', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>{T('Unique Items', 'عدد الأصناف')}</Typography>
                <Typography variant="h3" fontWeight="bold">{report.totalUniqueItems}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1, minWidth: 200, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>{T('Source Lines', 'مصادر التوريد')}</Typography>
                <Typography variant="h3" fontWeight="bold">{report.totalSourceLines}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1, minWidth: 200, background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', color: 'white', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>{T('Grand Total Value', 'إجمالي القيمة')}</Typography>
                <Typography variant="h4" fontWeight="bold">{fmtKWD(report.grandTotalValue)}</Typography>
              </CardContent>
            </Card>
          </Stack>

          <Paper sx={{ p: 1.5, mb: 2, borderRadius: 3 }}>
            <TextField size="small" fullWidth placeholder={T('Search item or source...', 'بحث صنف أو مصدر...')}
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </Paper>

          <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', color: 'white', fontWeight: 'bold' } }}>
                  <TableCell></TableCell>
                  <TableCell>{T('Item Code', 'كود الصنف')}</TableCell>
                  <TableCell>{T('Item Name', 'اسم الصنف')}</TableCell>
                  <TableCell>{T('Unit', 'الوحدة')}</TableCell>
                  <TableCell align="right">{T('Total Quantity', 'إجمالي الكمية')}</TableCell>
                  <TableCell align="right">{T('Total Value', 'إجمالي القيمة')}</TableCell>
                  <TableCell align="center">{T('Sources', 'عدد المصادر')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredItems.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">{T('No data for the selected filters', 'لا توجد بيانات للفلاتر المحددة')}</Typography>
                  </TableCell></TableRow>
                )}
                {filteredItems.map((it) => (
                  <React.Fragment key={it.itemId}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton size="small" onClick={() => setExpanded(p => ({ ...p, [it.itemId]: !p[it.itemId] }))}>
                          {expanded[it.itemId] ? <CollapseIcon /> : <ExpandIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>{it.itemCode}</TableCell>
                      <TableCell sx={{ color: 'text.primary' }}>{isRTL ? it.itemNameAr : it.itemName}</TableCell>
                      <TableCell><Chip size="small" label={isRTL ? it.unitNameAr : it.unitName} /></TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.primary' }}>{fmt(it.totalQuantity)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#10b981' }}>{fmtKWD(it.totalValue)}</TableCell>
                      <TableCell align="center"><Chip size="small" label={it.sources.length} color="primary" /></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ p: 0, borderBottom: 0 }}>
                        <Collapse in={expanded[it.itemId]} unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: isDark ? 'rgba(249,115,22,0.06)' : '#fff7ed' }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 'bold' }}>{T('Source Type', 'نوع المصدر')}</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>{T('Source', 'المصدر')}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{T('Quantity', 'الكمية')}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{T('Avg Unit Cost', 'متوسط التكلفة')}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{T('Total Value', 'القيمة الإجمالية')}</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>{T('Documents', 'عدد المستندات')}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {it.sources.map((s, i) => (
                                  <TableRow key={i}>
                                    <TableCell>
                                      <Chip size="small" icon={sourceIcon(s.sourceType)}
                                        label={isRTL ? s.sourceTypeAr : s.sourceType}
                                        sx={{ bgcolor: sourceColor(s.sourceType), color: 'white', fontWeight: 'bold' }} />
                                    </TableCell>
                                    <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>{isRTL ? s.sourceNameAr : s.sourceName}</TableCell>
                                    <TableCell align="right" sx={{ color: 'text.primary' }}>{fmt(s.quantity)}</TableCell>
                                    <TableCell align="right" sx={{ color: 'text.primary' }}>{fmt(s.avgUnitCost, 4)}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.primary' }}>{fmtKWD(s.totalValue)}</TableCell>
                                    <TableCell align="center" sx={{ color: 'text.primary' }}>{s.documentCount}</TableCell>
                                  </TableRow>
                                ))}
                                <TableRow sx={{ bgcolor: isDark ? 'rgba(16,185,129,0.12)' : '#ecfdf5' }}>
                                  <TableCell colSpan={2} sx={{ fontWeight: 'bold', color: 'text.primary' }}>{T('Item Total', 'إجمالي الصنف')}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.primary' }}>{fmt(it.totalQuantity)}</TableCell>
                                  <TableCell />
                                  <TableCell align="right" sx={{ fontWeight: 'bold', color: '#10b981' }}>{fmtKWD(it.totalValue)}</TableCell>
                                  <TableCell />
                                </TableRow>
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default BranchReceiptsReport;
